import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Slider,
  TextField,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { RELEASE_STATUS } from './flagDisplay';

const idsToText = (ids) => (ids || []).join('\n');
const textToIds = (text) =>
  text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * Changes who can see a flag.
 *
 * Only the control relevant to the chosen mode is shown, mirroring how the backend
 * resolves: the mode selects which targeting field is authoritative, and the others are
 * ignored. Showing them all would invite someone to set a rollout percentage that the
 * mode never reads and then wonder why nothing happened.
 */
const ReleaseModal = ({ open, node, onClose, onSubmit, submitting }) => {
  const row = (node && node.node && node.node.row) || {};
  const [releaseStatus, setReleaseStatus] = useState('OFF');
  const [schoolIds, setSchoolIds] = useState('');
  const [userIds, setUserIds] = useState('');
  const [rolloutPct, setRolloutPct] = useState(0);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open) return;
    setReleaseStatus(row.releaseStatus || 'OFF');
    setSchoolIds(idsToText(row.schoolIds));
    setUserIds(idsToText(row.userIds));
    setRolloutPct(row.rolloutPct || 0);
    setReason('');
  }, [open, row.key]);

  if (!node) return null;

  const goingWide = releaseStatus === 'ALL' && row.releaseStatus !== 'ALL';
  const goingBackwards =
    RELEASE_STATUS.indexOf(releaseStatus) < RELEASE_STATUS.indexOf(row.releaseStatus || 'OFF');

  // Targeting inputs belong to the modes that actually read them. OFF and ALL read
  // neither, and an editable field the backend ignores is how someone ends up setting a
  // rollout percentage and wondering why nothing happened.
  const targeting = releaseStatus === 'PARTIAL' || releaseStatus === 'CANARY';

  // The overlay is still *applied* in every mode, so hiding the input must not hide the
  // fact that people are on it. Under OFF especially: the flag reads as off for everyone
  // while these users can see the feature.
  const hiddenOverlayCount = !targeting ? textToIds(userIds).length : 0;

  const sameIds = (text, ids) =>
    textToIds(text).slice().sort().join(',') === (ids || []).slice().sort().join(',');

  // `reason` is metadata *about* a change, not a change in itself — typing one on its own
  // would write an audit row recording that nothing happened.
  const dirty =
    releaseStatus !== (row.releaseStatus || 'OFF') ||
    !sameIds(userIds, row.userIds) ||
    (releaseStatus === 'PARTIAL' && !sameIds(schoolIds, row.schoolIds)) ||
    (releaseStatus === 'CANARY' && Number(rolloutPct) !== (row.rolloutPct || 0));

  const submit = () => {
    const data = { releaseStatus, reason: reason || undefined };
    if (releaseStatus === 'PARTIAL') data.schoolIds = textToIds(schoolIds);
    if (releaseStatus === 'CANARY') data.rolloutPct = Number(rolloutPct);
    // The overlay is independent of mode, so it always goes.
    data.userIds = textToIds(userIds);
    onSubmit(row.key, data);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Release
        <Typography variant="body2" color="textSecondary" style={{ fontFamily: 'monospace' }}>
          {row.key}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {node.blockedByAncestor && (
          <Box mb={2}>
            <Alert severity="warning">
              <strong>{node.blockedByAncestor}</strong> is off or not live, so this stays
              invisible to everyone no matter what you set here. Release the ancestor first.
            </Alert>
          </Box>
        )}
        {node.narrowedByAncestor && (
          <Box mb={2}>
            <Alert severity="info">
              <strong>{node.narrowedByAncestor}</strong> is on a pilot or canary, so this can
              reach at most that ancestor's audience.
            </Alert>
          </Box>
        )}

        <Box mb={2}>
          <Typography variant="body2" gutterBottom>
            Release status
          </Typography>
          <Select
            fullWidth
            variant="outlined"
            margin="dense"
            value={releaseStatus}
            onChange={(e) => setReleaseStatus(e.target.value)}>
            {RELEASE_STATUS.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {releaseStatus === 'PARTIAL' && (
          <Box mb={2}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              variant="outlined"
              label="School IDs (one per line)"
              value={schoolIds}
              onChange={(e) => setSchoolIds(e.target.value)}
            />
          </Box>
        )}

        {releaseStatus === 'CANARY' && (
          <Box mb={2}>
            <Typography variant="body2" gutterBottom>
              Rollout: {rolloutPct}% of schools
            </Typography>
            <Slider
              value={rolloutPct}
              onChange={(_e, v) => setRolloutPct(v)}
              step={5}
              marks
              min={0}
              max={100}
              valueLabelDisplay="auto"
            />
            <Typography variant="caption" color="textSecondary">
              Bucketing is stable per school, so raising this only ever adds schools — it never
              swaps which ones are in.
            </Typography>
          </Box>
        )}

        {targeting && (
          <Box mb={2}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              variant="outlined"
              label="Always-on user IDs (one per line)"
              helperText="Additive in any mode — useful for seeing a feature while it is still at canary. Does not override a kill."
              value={userIds}
              onChange={(e) => setUserIds(e.target.value)}
            />
          </Box>
        )}

        {hiddenOverlayCount > 0 && (
          <Box mb={2}>
            <Alert severity="warning">
              <strong>{hiddenOverlayCount}</strong> user(s) are on the always-on list and will
              still see this
              {releaseStatus === 'OFF' ? ' even though it is off' : ''}. The list is kept as-is
              — switch to Partial or Canary to edit it.
            </Alert>
          </Box>
        )}

        <TextField
          fullWidth
          variant="outlined"
          label="Reason (recorded in the release log)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />

        {goingWide && (
          <Box mt={2}>
            <Alert severity="warning">
              This releases to <strong>every school</strong>. Consider a pilot first.
            </Alert>
          </Box>
        )}
        {goingBackwards && (
          <Box mt={2}>
            <Alert severity="info">
              Narrowing from <strong>{row.releaseStatus}</strong> to{' '}
              <strong>{releaseStatus}</strong>. Allowed, and recorded in the log — schools
              currently outside the new audience will lose the feature.
            </Alert>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={submit}
          disabled={submitting || !dirty}>
          {submitting ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReleaseModal;
